import { useState } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useNotification } from 'spindl-shared-components';

import { TestEmail } from '../../../modules/emailTemplates/types';
import { isEmail, isEmptyString } from '../../../lib/utils';
import { getTranslation } from '../../../lib/reactUtils';
import { NOTIFICATION_DURATION } from '../../../lib/constants';

import { SendEmailMenuCombinedProps } from './types';
import { sendTestEmailUseCase } from './SendEmailMenu.interactor';

const usePresenter = (props: SendEmailMenuCombinedProps): SendEmailMenuCombinedProps => {
  const { t } = useTranslation();
  const { trigger } = useNotification();

  const [errorText, setErrorText] = useState<string>('');
  const [testEmail, setTestEmail] = useState<string>('');

  const {
    emailTemplate,
    html,
    setShow,
  } = props;

  const sendTestEmail = useMutation((body: TestEmail) => {
    return sendTestEmailUseCase(Number(emailTemplate?.id), body);
  });

  const performSendTestEmail = async () => {
    if (!isEmail(testEmail)) {
      setErrorText(t('send_email_menu.invalid_email'));
      return;
    } else {
      if (setShow) {
        setShow(false);
      }
    }

    const failedMessage = getTranslation('send_email_menu.toast_failed');
    if (isEmptyString(emailTemplate?.senderEmail) || isEmptyString(emailTemplate?.senderName) || isEmptyString(html)) {
      trigger({
        message: failedMessage,
        type: 'Critical',
        duration: NOTIFICATION_DURATION,
      });
      return;
    }

    try {
      if (html) {
        const body: TestEmail = { receiver: testEmail, html: html };
        await sendTestEmail.mutateAsync(body);
        trigger({
          message: getTranslation('send_email_menu.toast_success', {
            email: testEmail,
          }),
          type: 'Positive',
          duration: NOTIFICATION_DURATION,
        });
      } else {

        trigger({
          message: failedMessage,
          type: 'Critical',
          duration: NOTIFICATION_DURATION,
        });
      }
    } catch (e) {
      trigger({
        message: failedMessage,
        type: 'Critical',
        duration: NOTIFICATION_DURATION,
      });
    }
  };

  const testEmailKeyDownHandler = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      await performSendTestEmail();
    }
  };

  const onTestEmailChanged = (text: string) => {
    setTestEmail(text);
    setErrorText('');
  };

  props = {
    ...props,
    type: errorText === '' ? 'None' : 'Error',
    textField: {
      ...props.textField,
      textInput: {
        ...props.textField?.textInput,
        onTextChanged: onTestEmailChanged,
        onKeyDown: testEmailKeyDownHandler,
      },
      text: {
        value: errorText === '' ? null : errorText,
      },
    },
    button: {
      ...props.button,
      onClick: performSendTestEmail,
      disabled: isEmptyString(testEmail),
    },
  };
  return props;
};

export default usePresenter;
